import { useState, useEffect } from "react";
import RunDetail from "./RunDetail";
import { formatDuration } from "./RunDetail/utils";

const PLAN_START = new Date(2026, 1, 16); // Monday Feb 16 — ISO week 8, plan week 1

// ─── Plan data ────────────────────────────────────────────────────────────────
const PHASES_META = [
  {
    number: 1,
    title: "Foundation",
    weeks: [1, 2, 3, 4],
    color: "#E8650A",
    light: "#FFF5EE",
    dates: "Feb 16 – Mar 15",
  },
  {
    number: 2,
    title: "Endurance",
    weeks: [5, 6, 7, 8, 9, 10],
    color: "#2E75B6",
    light: "#EEF4FB",
    dates: "Mar 16 – Apr 26",
  },
  {
    number: 3,
    title: "Race Specific",
    weeks: [11, 12, 13, 14, 15, 16],
    color: "#27AE60",
    light: "#EDFAF1",
    dates: "Apr 27 – Jun 7",
  },
  {
    number: 4,
    title: "Taper & Race",
    weeks: [17, 18, 19],
    color: "#C0392B",
    light: "#FDF0EF",
    dates: "Jun 8 – Jun 27",
  },
];

const WEEK_PLAN = {
  1: {
    runs: 3,
    km: 7.5,
    focus: "Learn the movements. Don't push pace.",
    recovery: false,
    days: [
      {
        day: "Mon",
        type: "strength+run",
        workout: "A",
        run: "Run/Walk × 12 reps (30 min)",
      },
      { day: "Tue", type: "rest" },
      {
        day: "Wed",
        type: "strength+run",
        workout: "A",
        run: "Run/Walk × 12 reps (30 min)",
      },
      { day: "Thu", type: "rest" },
      {
        day: "Fri",
        type: "strength+run",
        workout: "B",
        run: "Run/Walk × 12 reps (30 min)",
      },
      { day: "Sat", type: "rest" },
      { day: "Sun", type: "rest" },
    ],
  },
  2: {
    runs: 3,
    km: 8.4,
    focus: "Same structure, 2 more reps. Notice HR recovering faster.",
    recovery: false,
    days: [
      {
        day: "Mon",
        type: "strength+run",
        workout: "A",
        run: "Run/Walk × 14 reps (35 min)",
      },
      { day: "Tue", type: "rest" },
      {
        day: "Wed",
        type: "strength+run",
        workout: "A",
        run: "Run/Walk × 14 reps (35 min)",
      },
      { day: "Thu", type: "rest" },
      {
        day: "Fri",
        type: "strength+run",
        workout: "B",
        run: "Run/Walk × 14 reps (35 min)",
      },
      { day: "Sat", type: "rest" },
      { day: "Sun", type: "rest" },
    ],
  },
  3: {
    runs: 3,
    km: 9.9,
    focus: "Longer run intervals. Start to feel a rhythm.",
    recovery: false,
    days: [
      {
        day: "Mon",
        type: "strength+run",
        workout: "A",
        run: "2min run / 45s walk × 12 (33 min)",
      },
      { day: "Tue", type: "rest" },
      {
        day: "Wed",
        type: "strength+run",
        workout: "A",
        run: "2min run / 45s walk × 12 (33 min)",
      },
      { day: "Thu", type: "rest" },
      {
        day: "Fri",
        type: "strength+run",
        workout: "B",
        run: "2min run / 45s walk × 12 (33 min)",
      },
      { day: "Sat", type: "rest" },
      { day: "Sun", type: "rest" },
    ],
  },
  4: {
    runs: 3,
    km: 10.5,
    focus: "Recovery week — reduce effort. First continuous 3-min blocks.",
    recovery: true,
    days: [
      {
        day: "Mon",
        type: "strength+run",
        workout: "A",
        run: "3min run / 30s walk × 10 (35 min)",
      },
      { day: "Tue", type: "rest" },
      {
        day: "Wed",
        type: "strength+run",
        workout: "A",
        run: "3min run / 30s walk × 10 (35 min)",
      },
      { day: "Thu", type: "rest" },
      {
        day: "Fri",
        type: "strength+run",
        workout: "B",
        run: "3min run / 30s walk × 10 (35 min)",
      },
      { day: "Sat", type: "rest" },
      { day: "Sun", type: "rest" },
    ],
  },
  5: {
    runs: 4,
    km: 16,
    focus: "Walk breaks shrinking. Almost continuous running.",
    recovery: false,
    days: [
      { day: "Mon", type: "rest" },
      {
        day: "Tue",
        type: "strength+run",
        workout: "A",
        run: "5min run / 20s walk × 6 (32 min)",
      },
      { day: "Wed", type: "strength", workout: "B" },
      {
        day: "Thu",
        type: "strength+run",
        workout: "A",
        run: "5min run / 20s walk × 6 (32 min)",
      },
      { day: "Fri", type: "rest" },
      {
        day: "Sat",
        type: "strength+run",
        workout: "B",
        run: "Easy continuous 20 min",
      },
      { day: "Sun", type: "longrun", run: "Long run: 4 km – Zone 2 only" },
    ],
  },
  6: {
    runs: 4,
    km: 17,
    focus: "First distance-based long run. 4 km Sunday.",
    recovery: false,
    days: [
      { day: "Mon", type: "rest" },
      {
        day: "Tue",
        type: "strength+run",
        workout: "A",
        run: "Easy continuous 20 min",
      },
      { day: "Wed", type: "strength", workout: "B" },
      {
        day: "Thu",
        type: "strength+run",
        workout: "A",
        run: "Easy continuous 25 min",
      },
      { day: "Fri", type: "rest" },
      {
        day: "Sat",
        type: "strength+run",
        workout: "B",
        run: "Easy continuous 20 min",
      },
      { day: "Sun", type: "longrun", run: "Long run: 4 km – Zone 2 only" },
    ],
  },
  7: {
    runs: 4,
    km: 18,
    focus: "5 km long run milestone. No walk breaks.",
    recovery: false,
    days: [
      { day: "Mon", type: "rest" },
      {
        day: "Tue",
        type: "strength+run",
        workout: "A",
        run: "Easy continuous 25 min",
      },
      { day: "Wed", type: "strength", workout: "B" },
      {
        day: "Thu",
        type: "strength+run",
        workout: "A",
        run: "Easy continuous 25 min",
      },
      { day: "Fri", type: "rest" },
      {
        day: "Sat",
        type: "strength+run",
        workout: "B",
        run: "Easy continuous 25 min",
      },
      { day: "Sun", type: "longrun", run: "Long run: 5 km – Zone 2 only" },
    ],
  },
  8: {
    runs: 4,
    km: 15,
    focus: "Recovery week — reduce all runs by 20%.",
    recovery: true,
    days: [
      { day: "Mon", type: "rest" },
      {
        day: "Tue",
        type: "strength+run",
        workout: "A",
        run: "Easy continuous 20 min",
      },
      { day: "Wed", type: "strength", workout: "B" },
      {
        day: "Thu",
        type: "strength+run",
        workout: "A",
        run: "Easy continuous 20 min",
      },
      { day: "Fri", type: "rest" },
      {
        day: "Sat",
        type: "strength+run",
        workout: "B",
        run: "Easy continuous 20 min",
      },
      { day: "Sun", type: "longrun", run: "Long run: 5 km – Zone 2 only" },
    ],
  },
  9: {
    runs: 4,
    km: 22,
    focus: "7 km long run. Fitness visibly improved since week 1.",
    recovery: false,
    days: [
      { day: "Mon", type: "rest" },
      {
        day: "Tue",
        type: "strength+run",
        workout: "A",
        run: "Easy continuous 30 min",
      },
      { day: "Wed", type: "strength", workout: "B" },
      {
        day: "Thu",
        type: "strength+run",
        workout: "A",
        run: "Easy continuous 30 min",
      },
      { day: "Fri", type: "rest" },
      {
        day: "Sat",
        type: "strength+run",
        workout: "B",
        run: "Easy continuous 25 min",
      },
      { day: "Sun", type: "longrun", run: "Long run: 7 km – Zone 2 only" },
    ],
  },
  10: {
    runs: 4,
    km: 22,
    focus: "Consolidation — repeat week 9. Consistency is the goal.",
    recovery: false,
    days: [
      { day: "Mon", type: "rest" },
      {
        day: "Tue",
        type: "strength+run",
        workout: "A",
        run: "Easy continuous 30 min",
      },
      { day: "Wed", type: "strength", workout: "B" },
      {
        day: "Thu",
        type: "strength+run",
        workout: "A",
        run: "Easy continuous 30 min",
      },
      { day: "Fri", type: "rest" },
      {
        day: "Sat",
        type: "strength+run",
        workout: "B",
        run: "Easy continuous 25 min",
      },
      { day: "Sun", type: "longrun", run: "Long run: 7 km – Zone 2 only" },
    ],
  },
  11: {
    runs: 4,
    km: 22,
    focus: "First tempo run Wednesday. Zone 3 ({Z3}). Long run 8 km.",
    recovery: false,
    days: [
      {
        day: "Mon",
        type: "strength+run",
        workout: "A",
        run: "Easy 5 km – Zone 2",
      },
      { day: "Tue", type: "rest" },
      {
        day: "Wed",
        type: "strength+run",
        workout: "B",
        run: "Tempo 4 km – Zone 3 ({Z3})",
      },
      { day: "Thu", type: "rest" },
      {
        day: "Fri",
        type: "strength+run",
        workout: "A",
        run: "Easy 5 km – Zone 2",
      },
      { day: "Sat", type: "rest" },
      { day: "Sun", type: "longrun", run: "Long run: 8 km – Zone 2 only" },
    ],
  },
  12: {
    runs: 4,
    km: 23,
    focus: "9 km long run. You can smell the finish line from here.",
    recovery: false,
    days: [
      {
        day: "Mon",
        type: "strength+run",
        workout: "A",
        run: "Easy 5 km – Zone 2",
      },
      { day: "Tue", type: "rest" },
      {
        day: "Wed",
        type: "strength+run",
        workout: "B",
        run: "Tempo 4 km – Zone 3",
      },
      { day: "Thu", type: "rest" },
      {
        day: "Fri",
        type: "strength+run",
        workout: "A",
        run: "Easy 5–6 km – Zone 2",
      },
      { day: "Sat", type: "rest" },
      { day: "Sun", type: "longrun", run: "Long run: 9 km – Zone 2 only" },
    ],
  },
  13: {
    runs: 4,
    km: 24,
    focus: "10 km milestone. You can now finish the race.",
    recovery: false,
    days: [
      {
        day: "Mon",
        type: "strength+run",
        workout: "A",
        run: "Easy 5 km – Zone 2",
      },
      { day: "Tue", type: "rest" },
      {
        day: "Wed",
        type: "strength+run",
        workout: "B",
        run: "Tempo 5 km – Zone 3",
      },
      { day: "Thu", type: "rest" },
      {
        day: "Fri",
        type: "strength+run",
        workout: "A",
        run: "Easy 5 km – Zone 2",
      },
      { day: "Sat", type: "rest" },
      { day: "Sun", type: "longrun", run: "Long run: 10 km – Zone 2 only" },
    ],
  },
  14: {
    runs: 4,
    km: 25,
    focus: "Peak long run — 11 km. Confidence builder.",
    recovery: false,
    days: [
      {
        day: "Mon",
        type: "strength+run",
        workout: "A",
        run: "Easy 5 km – Zone 2",
      },
      { day: "Tue", type: "rest" },
      {
        day: "Wed",
        type: "strength+run",
        workout: "B",
        run: "Tempo 5 km – Zone 3",
      },
      { day: "Thu", type: "rest" },
      {
        day: "Fri",
        type: "strength+run",
        workout: "A",
        run: "Easy 6 km – Zone 2",
      },
      { day: "Sat", type: "rest" },
      { day: "Sun", type: "longrun", run: "Long run: 11 km – Zone 2 only" },
    ],
  },
  15: {
    runs: 4,
    km: 20,
    focus: "Recovery week — reduce volume 20–30%.",
    recovery: true,
    days: [
      {
        day: "Mon",
        type: "strength+run",
        workout: "A",
        run: "Easy 4 km – Zone 2",
      },
      { day: "Tue", type: "rest" },
      {
        day: "Wed",
        type: "strength+run",
        workout: "B",
        run: "Easy 4 km – Zone 2 (no tempo)",
      },
      { day: "Thu", type: "rest" },
      {
        day: "Fri",
        type: "strength+run",
        workout: "A",
        run: "Easy 4 km – Zone 2",
      },
      { day: "Sat", type: "rest" },
      { day: "Sun", type: "longrun", run: "Long run: 8 km – Zone 2 only" },
    ],
  },
  16: {
    runs: 4,
    km: 22,
    focus: "Taper begins. Phase 3 ends. Race is 3 weeks away.",
    recovery: false,
    days: [
      {
        day: "Mon",
        type: "strength+run",
        workout: "A",
        run: "Easy 4 km – Zone 2",
      },
      { day: "Tue", type: "rest" },
      {
        day: "Wed",
        type: "strength+run",
        workout: "B",
        run: "Easy 5 km – Zone 2",
      },
      { day: "Thu", type: "rest" },
      {
        day: "Fri",
        type: "strength+run",
        workout: "A",
        run: "Easy 4 km – Zone 2",
      },
      { day: "Sat", type: "rest" },
      { day: "Sun", type: "longrun", run: "Long run: 9 km – Zone 2 only" },
    ],
  },
  17: {
    runs: 3,
    km: 11,
    focus: "Volume drops sharply. Store that energy. Do not add extra runs.",
    recovery: false,
    days: [
      {
        day: "Mon",
        type: "strength+run",
        workout: "A",
        run: "Easy 4 km – Zone 2",
      },
      { day: "Tue", type: "rest" },
      {
        day: "Wed",
        type: "strength+run",
        workout: "B",
        run: "Easy 3 km – Zone 2",
      },
      { day: "Thu", type: "rest" },
      { day: "Fri", type: "run", run: "Easy 4 km – Zone 2" },
      { day: "Sat", type: "rest" },
      { day: "Sun", type: "rest" },
    ],
  },
  18: {
    runs: 3,
    km: 9,
    focus: "Very short, very easy. Sleep well. Eat well.",
    recovery: false,
    days: [
      {
        day: "Mon",
        type: "strength+run",
        workout: "A",
        run: "Easy 3 km – Zone 2",
      },
      { day: "Tue", type: "rest" },
      {
        day: "Wed",
        type: "strength+run",
        workout: "B",
        run: "Easy 3 km – Zone 2",
      },
      { day: "Thu", type: "rest" },
      { day: "Fri", type: "run", run: "Easy 3 km – Zone 2" },
      { day: "Sat", type: "rest" },
      { day: "Sun", type: "rest" },
    ],
  },
  19: {
    runs: 2,
    km: 4,
    focus: "Race week. No KB. Short runs only. June 27 — RACE DAY.",
    recovery: false,
    days: [
      { day: "Mon", type: "run", run: "Easy 2 km – very light shakeout" },
      { day: "Tue", type: "rest" },
      { day: "Wed", type: "run", run: "Easy 2 km – very light shakeout" },
      { day: "Thu", type: "rest" },
      { day: "Fri", type: "rest" },
      { day: "Sat", type: "rest" },
      {
        day: "Sun",
        type: "race",
        run: "RACE – Futóest Quarter Marathon 10.55 km",
      },
    ],
  },
};

const DAY_TYPE_CONFIG = {
  "strength+run": { color: "#E8650A", label: "KB + Run" },
  strength: { color: "#7EB3E0", label: "KB Only" },
  longrun: { color: "#1E88E5", label: "Long Run" },
  run: { color: "#E67E22", label: "Run Only" },
  race: { color: "#F9A825", label: "Race Day" },
  rest: { color: "#ccc", label: "Rest" },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getCurrentWeek() {
  const days = Math.floor((new Date() - PLAN_START) / 86400000);
  return Math.min(Math.max(Math.floor(days / 7) + 1, 1), 19);
}

function getWeekStart(weekNum) {
  const d = new Date(PLAN_START);
  d.setDate(d.getDate() + (weekNum - 1) * 7);
  return d;
}

function getWeekDateRange(weekNum) {
  const start = getWeekStart(weekNum);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const fmt = d => d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  return `${fmt(start)} – ${fmt(end)}`;
}

function fmtTime(seconds, format) {
  return format === "minutes"
    ? `${Math.round(seconds / 60)} min`
    : formatDuration(seconds);
}

function formatPace(paceMinPerKm) {
  if (!paceMinPerKm) return "—";
  const m = Math.floor(paceMinPerKm);
  const s = Math.round((paceMinPerKm - m) * 60);
  return `${m}:${s.toString().padStart(2, "0")} /km`;
}

const ZONE_COLORS = {
  1: "#95A5A6",
  2: "#27AE60",
  3: "#F39C12",
  4: "#E67E22",
  5: "#C0392B",
};
const ZONE_LABELS = { 1: "Z1", 2: "Z2 ✓", 3: "Z3", 4: "Z4", 5: "Z5" };

function zoneRange(z, cfg) {
  if (!cfg) return "";
  switch (z) {
    case 1: return `≤ ${cfg.z1_max} bpm`;
    case 2: return `${cfg.z1_max + 1}–${cfg.z2_max} bpm`;
    case 3: return `${cfg.z2_max + 1}–${cfg.z3_max} bpm`;
    case 4: return `${cfg.z3_max + 1}–${cfg.z4_max} bpm`;
    case 5: return `≥ ${cfg.z4_max + 1} bpm`;
    default: return "";
  }
}

function expandZoneRefs(text, cfg) {
  if (!text || !cfg) return text || "";
  return text
    .replace(/{Z1}/g, zoneRange(1, cfg))
    .replace(/{Z2}/g, zoneRange(2, cfg))
    .replace(/{Z3}/g, zoneRange(3, cfg))
    .replace(/{Z4}/g, zoneRange(4, cfg))
    .replace(/{Z5}/g, zoneRange(5, cfg))
    .replace(/{Z2_MAX}/g, String(cfg.z2_max));
}

function zoneFailureDesc(zone_dist) {
  const above = (zone_dist[3] || 0) + (zone_dist[4] || 0) + (zone_dist[5] || 0);
  const below = zone_dist[1] || 0;
  if (above > 15) return `${above.toFixed(0)}% above Z2`;
  if (below > 30) return `${below.toFixed(0)}% in recovery`;
  return "mixed zones";
}

function ZoneBadge({ zone }) {
  if (!zone) return <span style={{ fontSize: 11, color: "var(--text-faint)" }}>No HR</span>;
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: "bold",
        padding: "2px 7px",
        borderRadius: 10,
        background: ZONE_COLORS[zone] + "22",
        color: ZONE_COLORS[zone],
        border: `1px solid ${ZONE_COLORS[zone]}44`,
      }}
    >
      {ZONE_LABELS[zone]}
    </span>
  );
}

// ─── Overall tab ──────────────────────────────────────────────────────────────
// ─── Run row (tracked, with always-visible untrack button) ───────────────────
function RunRow({ run, onSelect, onToggle, timeFormat = "duration", selectable = false, selected = false }) {
  return (
    <div
      onClick={selectable ? onSelect : undefined}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 16px",
        borderTop: "1px solid var(--border-subtle)",
        background: selectable && selected ? "var(--bg-alt)" : "var(--bg-card)",
        cursor: "pointer",
      }}
    >
      {/* Checkbox in selection mode */}
      {selectable && (
        <div
          style={{
            width: 20,
            height: 20,
            borderRadius: 4,
            border: `2px solid ${selected ? "#2E75B6" : "var(--border)"}`,
            background: selected ? "#2E75B6" : "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {selected && <span style={{ color: "#fff", fontSize: 12, lineHeight: 1 }}>✓</span>}
        </div>
      )}
      {/* Name + stats */}
      <div style={{ flex: 1, minWidth: 0 }} onClick={!selectable ? onSelect : undefined}>
        <div
          style={{
            fontSize: 13,
            fontWeight: "bold",
            color: "var(--text-primary)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {run.name}
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 3, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: "var(--text-subtle)" }}>{run.distance_km} km</span>
          <span style={{ fontSize: 11, color: "var(--text-subtle)" }}>{fmtTime(run.moving_time, timeFormat)}</span>
          <span style={{ fontSize: 11, color: "var(--text-subtle)" }}>{formatPace(run.pace_min_per_km)}</span>
          {run.average_hr && (
            <span style={{ fontSize: 11, color: "var(--text-subtle)" }}>{Math.round(run.average_hr)} bpm</span>
          )}
        </div>
        {run.zone_dist && run.zone_dist[2] < 80 && (
          <div style={{ fontSize: 10, color: "#C0392B", marginTop: 3 }}>
            ⚠ Zone 2: {run.zone_dist[2]}% — {zoneFailureDesc(run.zone_dist)}
          </div>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        <ZoneBadge zone={run.zone} />
        {!selectable && (
          <>
            {/* Untrack button — always visible, tappable on mobile */}
            <button
              onClick={e => {
                e.stopPropagation();
                onToggle();
              }}
              title="Remove from progress"
              style={{
                fontSize: 11,
                padding: "3px 8px",
                borderRadius: 6,
                border: "1.5px solid var(--border)",
                background: "var(--bg-page)",
                color: "var(--text-faint)",
                cursor: "pointer",
                fontWeight: "bold",
                lineHeight: 1,
              }}
            >
              ✕
            </button>
            <span style={{ fontSize: 14, color: "#ccc" }} onClick={onSelect}>
              ›
            </span>
          </>
        )}
      </div>
    </div>
  );
}

function OverallTab({ runs, weeklyTotals, currentWeek, onSwitchPhase }) {
  const totalKm = runs.reduce((s, r) => s + r.distance_km, 0);
  const totalRuns = runs.length;
  const zone2Runs = runs.filter(r => r.zone2_compliant).length;
  const zone2Pct = totalRuns ? Math.round((zone2Runs / totalRuns) * 100) : 0;

  return (
    <div style={{ padding: "20px", maxWidth: 720, margin: "0 auto" }}>
      {/* Big stats */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 10,
          marginBottom: 24,
        }}
      >
        {[
          { label: "Total Runs", value: totalRuns },
          { label: "Total km", value: `${totalKm.toFixed(1)} km` },
          { label: "Zone 2", value: `${zone2Pct}%`, sub: "of all runs" },
        ].map((s, i) => (
          <div
            key={i}
            style={{
              background: "var(--bg-card)",
              border: "1.5px solid var(--border)",
              borderRadius: 10,
              padding: "16px 12px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 24, fontWeight: "bold", color: "var(--text-primary)" }}>{s.value}</div>
            <div style={{ fontSize: 11, color: "var(--text-subtle)", marginTop: 3 }}>{s.label}</div>
            {s.sub && <div style={{ fontSize: 10, color: "var(--text-faint)", marginTop: 1 }}>{s.sub}</div>}
          </div>
        ))}
      </div>

      {/* Phase cards */}
      <div
        style={{
          fontSize: 12,
          color: "var(--text-subtle)",
          letterSpacing: 1,
          marginBottom: 10,
          fontFamily: "monospace",
        }}
      >
        PHASES
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {PHASES_META.map(phase => {
          const phaseRuns = runs.filter(r => phase.weeks.includes(r.week));
          const completedWeeks = phase.weeks.filter(w => w < currentWeek && weeklyTotals[w]).length;
          const isActive = phase.weeks.includes(currentWeek);
          const isDone = phase.weeks[phase.weeks.length - 1] < currentWeek;
          const isFuture = phase.weeks[0] > currentWeek;
          const pct = Math.round((completedWeeks / phase.weeks.length) * 100);

          return (
            <div
              key={phase.number}
              onClick={() => !isFuture && onSwitchPhase(phase.number)}
              style={{
                background: "var(--bg-card)",
                border: `1.5px solid ${isActive ? phase.color : "var(--border)"}`,
                borderRadius: 10,
                padding: "14px 16px",
                cursor: isFuture ? "default" : "pointer",
                opacity: isFuture ? 0.45 : 1,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 10,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: "bold", color: "var(--text-primary)" }}>
                    Phase {phase.number} — {phase.title}
                  </span>
                  {isActive && (
                    <span
                      style={{
                        fontSize: 10,
                        background: phase.color,
                        color: "#fff",
                        padding: "2px 8px",
                        borderRadius: 10,
                        fontWeight: "bold",
                      }}
                    >
                      ACTIVE
                    </span>
                  )}
                  {isDone && (
                    <span
                      style={{
                        fontSize: 10,
                        background: "#27AE60",
                        color: "#fff",
                        padding: "2px 8px",
                        borderRadius: 10,
                        fontWeight: "bold",
                      }}
                    >
                      ✓ DONE
                    </span>
                  )}
                </div>
                <span style={{ fontSize: 12, color: "var(--text-faint)" }}>
                  {isFuture
                    ? "upcoming"
                    : `${phaseRuns.length} runs · ${phaseRuns.reduce((s, r) => s + r.distance_km, 0).toFixed(1)} km`}
                </span>
              </div>
              <div
                style={{
                  background: "#F0EDE8",
                  borderRadius: 99,
                  height: 6,
                  overflow: "hidden",
                  marginBottom: 6,
                }}
              >
                <div
                  style={{
                    background: phase.color,
                    height: "100%",
                    width: `${pct}%`,
                    borderRadius: 99,
                  }}
                />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 11, color: "var(--text-faint)" }}>{phase.dates}</span>
                <span style={{ fontSize: 11, color: "var(--text-faint)" }}>
                  {completedWeeks}/{phase.weeks.length} weeks {!isFuture && "→"}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Phase tab ────────────────────────────────────────────────────────────────
function PhaseTab({ phase, runs, currentWeek, onSelectRun, onToggleTracked, allRunsMap, timeFormat = "duration", gpxSelecting = false, gpxSelected, onToggleGpxSelect, zoneConfig }) {
  const [expandedWeeks, setExpandedWeeks] = useState(() => {
    if (phase.weeks.includes(currentWeek)) return new Set([currentWeek]);
    return new Set();
  });

  function toggleWeek(weekNum) {
    setExpandedWeeks(prev => {
      const next = new Set(prev);
      if (next.has(weekNum)) next.delete(weekNum);
      else next.add(weekNum);
      return next;
    });
  }

  return (
    <div style={{ padding: "20px", maxWidth: 720, margin: "0 auto" }}>
      {/* GPX selection hint */}
      {gpxSelecting && (
        <div style={{ background: "#0D2137", border: "1px solid #2E75B6", borderRadius: 8, padding: "8px 14px", marginBottom: 12, fontSize: 12, color: "rgba(255,255,255,0.6)", textAlign: "center" }}>
          Tap runs to select for GPX export
        </div>
      )}
      {/* Phase header */}
      <div
        style={{
          background: phase.light,
          border: `1.5px solid ${phase.color}44`,
          borderRadius: 10,
          padding: "12px 16px",
          marginBottom: 16,
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: phase.color,
            fontWeight: "bold",
            letterSpacing: 1,
            marginBottom: 4,
          }}
        >
          {phase.dates}
        </div>
        <div style={{ fontSize: 13, color: "#444" }}>
          {phase.weeks.length} weeks ·{" "}
          {phase.weeks.reduce((s, w) => s + (WEEK_PLAN[w]?.km || 0), 0)} km planned
        </div>
      </div>

      {/* Week cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {phase.weeks.map(weekNum => {
          const plan = WEEK_PLAN[weekNum];
          const weekRuns = runs.filter(r => r.week === weekNum);
          const isCurrentWeek = weekNum === currentWeek;
          const isPast = weekNum < currentWeek;
          const isFuture = weekNum > currentWeek;
          const isExpanded = expandedWeeks.has(weekNum);
          const totalKm = weekRuns.reduce((s, r) => s + r.distance_km, 0);
          const zone2Pct = weekRuns.length
            ? Math.round((weekRuns.filter(r => r.zone2_compliant).length / weekRuns.length) * 100)
            : null;

          return (
            <div
              key={weekNum}
              style={{
                border: `1.5px solid ${isCurrentWeek ? phase.color : "var(--border)"}`,
                borderRadius: 10,
                overflow: "hidden",
                opacity: isFuture ? 0.45 : 1,
                background: "var(--bg-card)",
              }}
            >
              {/* Week header */}
              <div
                onClick={() => !isFuture && toggleWeek(weekNum)}
                style={{
                  padding: "12px 16px",
                  cursor: isFuture ? "default" : "pointer",
                  background: isCurrentWeek ? phase.light : "var(--bg-card)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 3,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 14,
                          fontWeight: "bold",
                          color: "var(--text-primary)",
                        }}
                      >
                        Week {weekNum}
                      </span>
                      {plan.recovery && (
                        <span
                          style={{
                            fontSize: 10,
                            background: "#E3F2FD",
                            color: "#1565C0",
                            padding: "1px 7px",
                            borderRadius: 10,
                            fontWeight: "bold",
                          }}
                        >
                          RECOVERY
                        </span>
                      )}
                      {isCurrentWeek && (
                        <span
                          style={{
                            fontSize: 10,
                            background: phase.color,
                            color: "#fff",
                            padding: "1px 7px",
                            borderRadius: 10,
                            fontWeight: "bold",
                          }}
                        >
                          NOW
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text-faint)" }}>{getWeekDateRange(weekNum)}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        justifyContent: "flex-end",
                        flexWrap: "wrap",
                        marginBottom: 3,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 12,
                          color: weekRuns.length >= plan.runs ? "#27AE60" : "#888",
                          fontWeight: "bold",
                        }}
                      >
                        {weekRuns.length}/{plan.runs} runs
                      </span>
                      <span style={{ fontSize: 12, color: "var(--text-subtle)" }}>
                        {totalKm.toFixed(1)}/{plan.km} km
                      </span>
                    </div>
                    {zone2Pct !== null && (
                      <span
                        style={{
                          fontSize: 11,
                          color:
                            zone2Pct >= 80 ? "#27AE60" : zone2Pct >= 50 ? "#F39C12" : "#C0392B",
                        }}
                      >
                        Zone 2: {zone2Pct}%
                      </span>
                    )}
                    {!isFuture && (
                      <div style={{ fontSize: 11, color: "#ccc", marginTop: 2 }}>
                        {isExpanded ? "▲" : "▼"}
                      </div>
                    )}
                  </div>
                </div>

                {/* Focus */}
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--text-muted)",
                    marginTop: 6,
                    fontStyle: "italic",
                  }}
                >
                  {expandZoneRefs(plan.focus, zoneConfig)}
                </div>

                {/* Mini progress bar */}
                {!isFuture && (
                  <div
                    style={{
                      background: "#F0EDE8",
                      borderRadius: 99,
                      height: 4,
                      overflow: "hidden",
                      marginTop: 8,
                    }}
                  >
                    <div
                      style={{
                        background: weekRuns.length >= plan.runs ? "#27AE60" : phase.color,
                        height: "100%",
                        width: `${Math.min(100, (weekRuns.length / plan.runs) * 100)}%`,
                        borderRadius: 99,
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Expanded: planned days + actual runs */}
              {isExpanded && (
                <div style={{ borderTop: `1px solid ${phase.color}22` }}>
                  {/* Planned schedule */}
                  <div style={{ padding: "12px 16px", background: "var(--bg-alt)" }}>
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--text-faint)",
                        letterSpacing: 1,
                        marginBottom: 8,
                        fontFamily: "monospace",
                      }}
                    >
                      PLANNED
                    </div>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {plan.days.map((d, i) => {
                        const cfg = DAY_TYPE_CONFIG[d.type];
                        return (
                          <div
                            key={i}
                            style={{
                              flex: "1 1 0",
                              minWidth: 38,
                              maxWidth: 80,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 10,
                                color: "var(--text-subtle)",
                                textAlign: "center",
                                marginBottom: 3,
                                fontFamily: "monospace",
                              }}
                            >
                              {d.day}
                            </div>
                            <div
                              style={{
                                background: d.type === "rest" ? "var(--bg-subtle)" : cfg.color + "18",
                                border: `1.5px solid ${d.type === "rest" ? "var(--border)" : cfg.color + "55"}`,
                                borderRadius: 6,
                                padding: "5px 3px",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 9,
                                  color: d.type === "rest" ? "#bbb" : cfg.color,
                                  fontWeight: "bold",
                                  lineHeight: 1.2,
                                }}
                              >
                                {d.type === "rest"
                                  ? "—"
                                  : d.workout
                                    ? `Wkt ${d.workout}`
                                    : d.type === "longrun"
                                      ? "LONG"
                                      : d.type === "race"
                                        ? "🏁"
                                        : "RUN"}
                              </div>
                              {d.run && (
                                <div
                                  style={{
                                    fontSize: 8,
                                    color: "var(--text-subtle)",
                                    marginTop: 2,
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {d.run
                                    .replace(/^(Long run:|Easy|Tempo|Run\/Walk)/i, "")
                                    .trim()
                                    .split("–")[0]
                                    .trim()
                                    .substring(0, 8)}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Actual runs */}
                  <div style={{ borderTop: "1px solid var(--border-subtle)" }}>
                    <div
                      style={{
                        padding: "10px 16px 6px",
                        fontSize: 11,
                        color: "var(--text-faint)",
                        letterSpacing: 1,
                        fontFamily: "monospace",
                      }}
                    >
                      YOUR RUNS {weekRuns.length === 0 && "— none yet"}
                    </div>
                    {weekRuns.length === 0 ? (
                      <div
                        style={{
                          padding: "8px 16px 14px",
                          fontSize: 13,
                          color: "var(--text-faint)",
                          fontStyle: "italic",
                        }}
                      >
                        {isPast
                          ? "No runs logged this week."
                          : "Runs will appear here after you sync."}
                      </div>
                    ) : (
                      weekRuns.map(run => (
                        <RunRow
                          key={run.strava_id}
                          run={run}
                          onSelect={gpxSelecting ? () => onToggleGpxSelect(String(run.strava_id)) : () => onSelectRun(run.strava_id)}
                          onToggle={() => onToggleTracked(run.strava_id, true)}
                          timeFormat={timeFormat}
                          selectable={gpxSelecting}
                          selected={gpxSelected?.has(String(run.strava_id))}
                        />
                      ))
                    )}
                    {/* Untracked runs this week — synced but excluded from progress */}
                    {(() => {
                      const PLAN_MS = Date.UTC(2026, 1, 16);
                      const weekStartMs = PLAN_MS + (weekNum - 1) * 7 * 86400000;
                      const weekEndMs = weekStartMs + 7 * 86400000;
                      const untrackedThisWeek = Object.entries(allRunsMap).filter(([id, r]) => {
                        if (r.tracked) return false;
                        const d = r.start_date ? r.start_date.slice(0, 10) : null;
                        if (!d) return false;
                        const [y, m, day] = d.split("-").map(Number);
                        const ms = Date.UTC(y, m - 1, day);
                        return ms >= weekStartMs && ms < weekEndMs;
                      });
                      if (untrackedThisWeek.length === 0) return null;
                      return (
                        <div style={{ borderTop: "1px solid var(--border-subtle)" }}>
                          <div
                            style={{
                              padding: "8px 16px 4px",
                              fontSize: 10,
                              color: "var(--text-faint)",
                              letterSpacing: 1,
                              fontFamily: "monospace",
                            }}
                          >
                            NOT TRACKED ({untrackedThisWeek.length})
                          </div>
                          {untrackedThisWeek.map(([id, r]) => (
                            <div
                              key={id}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                padding: "8px 16px",
                                borderTop: "1px solid var(--border-subtle)",
                                background: "var(--bg-alt)",
                                opacity: 0.65,
                              }}
                            >
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div
                                  style={{
                                    fontSize: 13,
                                    color: "var(--text-subtle)",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    textDecoration: "line-through",
                                  }}
                                >
                                  {r.name}
                                </div>
                                <div style={{ fontSize: 11, color: "var(--text-faint)", marginTop: 2 }}>
                                  {r.distance_km} km · {fmtTime(r.moving_time, timeFormat)}
                                </div>
                              </div>
                              <button
                                onClick={() => onToggleTracked(id, false)}
                                style={{
                                  fontSize: 11,
                                  padding: "3px 10px",
                                  borderRadius: 6,
                                  border: "1.5px solid #27AE60",
                                  background: "#EDFAF1",
                                  color: "#27AE60",
                                  cursor: "pointer",
                                  fontWeight: "bold",
                                  flexShrink: 0,
                                }}
                              >
                                + Track
                              </button>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Progress component ──────────────────────────────────────────────────
export default function Progress({ stravaId, onRunsLoaded, timeFormat = "duration", zoneConfigId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overall");
  const [selectedRun, setSelectedRun] = useState(null);
  const [gpxSelecting, setGpxSelecting] = useState(false);
  const [gpxSelected, setGpxSelected] = useState(new Set());
  const [gpxMenuOpen, setGpxMenuOpen] = useState(false);
  const currentWeek = getCurrentWeek();
  const currentPhase = PHASES_META.find(p => p.weeks.includes(currentWeek));

  useEffect(() => {
    if (!stravaId) return;
    fetchProgress();
  }, [stravaId, zoneConfigId]);

  async function fetchProgress() {
    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const [progressRes, allRes] = await Promise.all([
        fetch(`/api/progress/${stravaId}${zoneConfigId ? `?zoneConfigId=${zoneConfigId}` : ""}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`/api/runs/${stravaId}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const json = await progressRes.json();
      const allRuns = await allRes.json();
      // Build lookup: strava_id -> { tracked, name, distance, start_date, moving_time }
      const allRunsMap = {};
      (allRuns || []).forEach(r => {
        allRunsMap[r.strava_id] = {
          tracked: r.tracked !== 0,
          name: r.name,
          distance_km: parseFloat((r.distance / 1000).toFixed(2)),
          start_date: r.start_date,
          moving_time: r.moving_time,
        };
      });
      setData({ ...json, allRunsMap });
      if (onRunsLoaded && json.runs) onRunsLoaded(json.runs);
    } catch {
      setError("Failed to load progress");
    } finally {
      setLoading(false);
    }
  }

  async function toggleTracked(runId, currentlyTracked) {
    try {
      await fetch(`/api/runs/${stravaId}/${runId}/tracked`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ tracked: !currentlyTracked }),
      });
      await fetchProgress();
    } catch {
      setError("Failed to update run");
    }
  }

  async function syncRuns() {
    setSyncing(true);
    try {
      await fetch(`/api/runs/sync/${stravaId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("auth_token")}` },
      });
      await fetchProgress();
    } catch {
      setError("Sync failed");
    } finally {
      setSyncing(false);
    }
  }

  function toggleGpxSelect(id) {
    setGpxSelected(prev => {
      const next = new Set(prev);
      if (next.has(String(id))) next.delete(String(id));
      else next.add(String(id));
      return next;
    });
  }

  function exitGpxMode() {
    setGpxSelecting(false);
    setGpxSelected(new Set());
    setGpxMenuOpen(false);
  }

  async function downloadGpx(ids = null) {
    const token = localStorage.getItem("auth_token");
    const qs = ids && ids.size > 0 ? `?ids=${[...ids].join(",")}` : "";
    const res = await fetch(`/api/runs/${stravaId}/export/gpx${qs}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    let filename = "all-runs.gpx";
    if (ids && ids.size === 1) {
      const runId = [...ids][0];
      const run = (data?.runs || []).find(r => String(r.strava_id) === runId);
      const safeName = run?.name ? run.name.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase() : runId;
      filename = `${safeName}.gpx`;
    } else if (ids && ids.size > 1) {
      filename = "selected-runs.gpx";
    }
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (selectedRun) {
    return (
      <RunDetail stravaId={stravaId} runId={selectedRun} onBack={() => setSelectedRun(null)} zoneConfig={data?.zone_config} />
    );
  }

  if (!stravaId) {
    return (
      <div
        style={{
          padding: "60px 20px",
          textAlign: "center",
          maxWidth: 480,
          margin: "0 auto",
        }}
      >
        <div style={{ fontSize: 48, marginBottom: 16 }}>🏃</div>
        <div
          style={{
            fontSize: 22,
            fontWeight: "bold",
            color: "var(--text-primary)",
            marginBottom: 8,
          }}
        >
          Track Your Progress
        </div>
        <div
          style={{
            fontSize: 14,
            color: "var(--text-muted)",
            marginBottom: 32,
            lineHeight: 1.6,
          }}
        >
          Connect your Strava account to track your runs against the training plan, monitor heart
          rate zones, and see your progress across all 19 weeks.
        </div>
        <a
          href={`/api/auth/strava?token=${encodeURIComponent(localStorage.getItem("auth_token"))}`}
          style={{
            display: "inline-block",
            background: "#FC4C02",
            color: "#fff",
            padding: "14px 32px",
            borderRadius: 10,
            fontFamily: "'Georgia', serif",
            fontSize: 15,
            fontWeight: "bold",
            textDecoration: "none",
          }}
        >
          Connect with Strava
        </a>
      </div>
    );
  }

  if (loading)
    return (
      <div style={{ padding: 40, textAlign: "center", color: "var(--text-subtle)" }}>Loading your runs...</div>
    );
  if (error)
    return <div style={{ padding: 40, textAlign: "center", color: "#C0392B" }}>{error}</div>;

  const runs = data?.runs || [];
  const weeklyTotals = data?.weekly_totals || {};
  const allRunsMap = data?.allRunsMap || {};
  const currentWeekRuns = runs.filter(r => r.week === currentWeek);
  const currentPlan = WEEK_PLAN[currentWeek];

  const TABS = [
    { key: "overall", label: "Overall" },
    ...PHASES_META.map(p => ({
      key: `phase${p.number}`,
      label: `Phase ${p.number}`,
      phase: p,
    })),
  ];

  const activePhase = PHASES_META.find(p => `phase${p.number}` === activeTab);
  const tabColor = activePhase?.color || "#1A1A2E";

  return (
    <div
      style={{
        fontFamily: "'Georgia', serif",
        background: "var(--bg-page)",
        minHeight: "100vh",
        paddingBottom: 40,
      }}
    >
      {/* Header */}
      <div style={{ background: "#1A1A2E", padding: "20px 20px 0" }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
              flexWrap: "wrap",
              gap: 10,
            }}
          >
            <div>
              <div
                style={{
                  color: "rgba(255,255,255,0.5)",
                  fontSize: 11,
                  letterSpacing: 2,
                  fontFamily: "monospace",
                }}
              >
                TRAINING PROGRESS
              </div>
              <div
                style={{
                  color: "#fff",
                  fontSize: 18,
                  fontWeight: "bold",
                  marginTop: 3,
                }}
              >
                Week {currentWeek} of 19 · {currentPhase?.title}
              </div>
              <div
                style={{
                  color: "rgba(255,255,255,0.4)",
                  fontSize: 11,
                  marginTop: 2,
                }}
              >
                {currentWeekRuns.length}/{currentPlan?.runs || 0} runs this week ·{" "}
                {currentWeekRuns.reduce((s, r) => s + r.distance_km, 0).toFixed(1)}/
                {currentPlan?.km || 0} km
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {gpxSelecting ? (
                <>
                  <button
                    onClick={exitGpxMode}
                    style={{
                      background: "transparent",
                      border: "1.5px solid rgba(255,255,255,0.3)",
                      borderRadius: 8,
                      color: "rgba(255,255,255,0.6)",
                      padding: "8px 14px",
                      fontFamily: "'Georgia', serif",
                      fontSize: 13,
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    disabled={gpxSelected.size === 0}
                    onClick={() => { downloadGpx(gpxSelected); exitGpxMode(); }}
                    style={{
                      background: gpxSelected.size > 0 ? "#2E75B6" : "#333",
                      border: "none",
                      borderRadius: 8,
                      color: "#fff",
                      padding: "8px 14px",
                      fontFamily: "'Georgia', serif",
                      fontSize: 13,
                      cursor: gpxSelected.size > 0 ? "pointer" : "default",
                      fontWeight: "bold",
                    }}
                  >
                    ↓ {gpxSelected.size > 0 ? `${gpxSelected.size} run${gpxSelected.size !== 1 ? "s" : ""}` : "Select runs"}
                  </button>
                </>
              ) : (
                <>
                  <div style={{ position: "relative" }}>
                    <button
                      onClick={() => setGpxMenuOpen(p => !p)}
                      style={{
                        background: "#2E75B6",
                        border: "none",
                        borderRadius: 8,
                        color: "#fff",
                        padding: "8px 14px",
                        fontFamily: "'Georgia', serif",
                        fontSize: 13,
                        cursor: "pointer",
                        fontWeight: "bold",
                      }}
                    >
                      ↓ GPX ▾
                    </button>
                    {gpxMenuOpen && (
                      <div
                        style={{
                          position: "absolute",
                          top: "calc(100% + 4px)",
                          right: 0,
                          background: "#1A1A2E",
                          border: "1px solid #2A2A4E",
                          borderRadius: 8,
                          overflow: "hidden",
                          zIndex: 100,
                          minWidth: 160,
                        }}
                      >
                        <button
                          onClick={() => { downloadGpx(); setGpxMenuOpen(false); }}
                          style={{ display: "block", width: "100%", padding: "11px 16px", background: "transparent", border: "none", color: "#fff", fontFamily: "'Georgia', serif", fontSize: 13, cursor: "pointer", textAlign: "left" }}
                        >
                          ↓ Download all
                        </button>
                        <button
                          onClick={() => { setGpxSelecting(true); setGpxMenuOpen(false); }}
                          style={{ display: "block", width: "100%", padding: "11px 16px", background: "transparent", border: "none", color: "#fff", fontFamily: "'Georgia', serif", fontSize: 13, cursor: "pointer", textAlign: "left", borderTop: "1px solid #2A2A4E" }}
                        >
                          ☑ Select runs…
                        </button>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={syncRuns}
                    disabled={syncing}
                    style={{
                      background: syncing ? "#333" : "#FC4C02",
                      border: "none",
                      borderRadius: 8,
                      color: "#fff",
                      padding: "8px 14px",
                      fontFamily: "'Georgia', serif",
                      fontSize: 13,
                      cursor: syncing ? "default" : "pointer",
                      fontWeight: "bold",
                    }}
                  >
                    {syncing ? "Syncing..." : "↻ Sync"}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Inner tab bar */}
          <div
            style={{
              display: "flex",
              gap: 0,
              overflowX: "auto",
              scrollbarWidth: "none",
            }}
          >
            {TABS.map(tab => {
              const isActive = activeTab === tab.key;
              const color = tab.phase?.color || "#FC4C02";
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    flex: "0 0 auto",
                    padding: "9px 14px",
                    border: "none",
                    borderBottom: isActive ? `3px solid ${color}` : "3px solid transparent",
                    background: "transparent",
                    color: isActive ? "#fff" : "rgba(255,255,255,0.4)",
                    fontFamily: "'Georgia', serif",
                    fontSize: 13,
                    cursor: "pointer",
                    fontWeight: isActive ? "bold" : "normal",
                    whiteSpace: "nowrap",
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab content */}
      {activeTab === "overall" && (
        <OverallTab
          runs={runs}
          weeklyTotals={weeklyTotals}
          currentWeek={currentWeek}
          onSwitchPhase={n => setActiveTab(`phase${n}`)}
        />
      )}
      {PHASES_META.map(
        phase =>
          activeTab === `phase${phase.number}` && (
            <PhaseTab
              key={phase.number}
              phase={phase}
              runs={runs}
              currentWeek={currentWeek}
              onSelectRun={setSelectedRun}
              onToggleTracked={toggleTracked}
              allRunsMap={allRunsMap}
              timeFormat={timeFormat}
              gpxSelecting={gpxSelecting}
              gpxSelected={gpxSelected}
              onToggleGpxSelect={toggleGpxSelect}
              zoneConfig={data?.zone_config}
            />
          )
      )}
    </div>
  );
}
